import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosDatePicker } from "@/components/UiKit"
import type { PriceChangeSearchFilters } from "@/screens/Pricing/stores"

type FormValues = PriceChangeSearchFilters & {
  dateFrom?: string | null
  dateTo?: string | null
}

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
}

const PurchasingCostHistoryFilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const currentSearch = useSearch({
    from: "/app/pricing/_layout/history/purchase-cost",
    strict: true,
  })

  const navigate = useNavigate({
    from: "/app/pricing/history/purchase-cost",
  })

  const { dateFrom, dateTo, changeType } = currentSearch

  const methods = useForm<FormValues>({
    values: {
      dateFrom: dateFrom,
      dateTo: dateTo,
      changeType,
    },
  })

  const handleFilterChange = async (name: string, value: unknown) => {
    await navigate({
      search: (prev) => {
        const val = Array.isArray(value) && value.length === 0 ? undefined : value

        return {
          ...prev,
          [name]: val || undefined,
        }
      },
      replace: true,
    })
  }

  const handleFilterClear = async () => {
    await navigate({
      search: (prev) => {
        return {
          ...prev,
          dateFrom: undefined,
          dateTo: undefined,
        }
      },
      replace: true,
    })
  }

  return (
    <SearchFilterDrawer
      open={showFilterDrawer}
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      clear={handleFilterClear}
      isClearButtonEnabled={Boolean(dateFrom) || Boolean(dateTo)}
      testId='purchase-cost-history.filter-drawer'
    >
      <FormProvider {...methods}>
        <Box className='flex h-full flex-col gap-4 p-4'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Date Changed
          </Typography>
          <Divider className='mb-4' />
          <Box className='flex flex-col gap-4 px-4'>
            <AerosDatePicker
              label='From'
              value={dateFrom ?? ""}
              onChange={(value) => handleFilterChange("dateFrom", value)}
              data-testid='purchase-cost-history.date-range-filter.date-from-field'
            />
            <AerosDatePicker
              label='To'
              value={dateTo ?? ""}
              onChange={(value) => handleFilterChange("dateTo", value)}
              data-testid='purchase-cost-history.date-range-filter.date-to-field'
            />
          </Box>
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}

export default PurchasingCostHistoryFilterDrawer
